import * as a from '@mitranim/js/all.mjs'
import * as ob from '@mitranim/js/obs.mjs'
import * as c from './conf.mjs'
import * as ue from './util-err.mjs'
import * as ufe from './util-feed.mjs'
import * as ut from './util-time.mjs'
import * as um from './util-misc.mjs'
import * as ul from './util-log.mjs'

// WTB better names.
export function obsTask(obs) {return obs.valueTask()}
export function obsAll(vals) {return um.PromiseDict.mapDict(vals, obsTask)}
export function entsTask(obs) {return obs.valueTask().then(ufe.pageEnts)}
export function obsAllEnts(vals) {return um.PromiseDict.mapDict(vals, entsTask)}

/*
Observable specialized for storing `{task, error, value}`: a common pattern for
the state of async operations such as network requests.
*/
export class TaskObs extends ob.DeObs {
  constructor() {
    super()
    this.value = undefined
    this.error = undefined
    this.task = undefined
    a.priv(this, `setAt`)
    a.priv(this, `readAt`)
  }

  setValue(value) {
    this.value = value
    this.setAt = Date.now()
    return value
  }

  setError(error) {this.error = error}

  setTask(task) {
    if (a.isNil(task)) return this.task = undefined
    return this.task = new ObsTask(this, task)
  }

  onValue(value) {
    ob.Sched.main.pause()
    try {
      this.setValue(value)
      this.setError()
      this.setTask()
      return this.value
    }
    finally {ob.Sched.main.resume()}
  }

  onError(error) {
    ob.Sched.main.pause()
    try {
      this.setTask()
      this.setError(error)
    }
    finally {ob.Sched.main.resume()}
  }

  /*
  Questionable. Note: this intentionally doesn't clear `.task` because this
  wouldn't cancel the task.
  */
  clear() {
    ob.Sched.main.pause()
    try {
      this.value = undefined
      this.error = undefined
      this.setAt = undefined
      this.readAt = undefined
    }
    finally {ob.Sched.main.resume()}
  }

  toJSON() {return this.value}
  hasValue() {return a.isFin(this.setAt)}
  hasError() {return a.truthy(this.error)}
  hasTask() {return a.truthy(this.task)}
  isDone() {return !this.hasTask() && this.hasValue()}
  isOutdated(freshness) {return ut.since(this.setAt) > a.laxFin(freshness)}
  isForgotten(freshness) {return ut.since(this.readAt) > a.laxFin(freshness)}
  idle() {return this.hasTask() ? undefined : this}
  empty() {return this.hasTask() || this.hasValue() ? undefined : this}
  then(...val) {return this.async().then(...val)}
  async wait() {await a.optInst(this.task, ObsTask)?.wait()}

  // TODO better name.
  async async() {
    const {task} = this
    if (task) return task

    const {error} = this
    if (error) throw error

    return this.value
  }

  static get ph() {return TaskObsPh}
}

// For client-only usage. Avoid in SSR code.
export class TaskObsLog extends TaskObs {
  onError(err) {
    super.onError(err)
    if (err) ul.logErr(err)
  }
}

// For client-only usage. Avoid in SSR code.
export class TaskObsNotif extends TaskObs {
  onError(err) {
    super.onError(err)
    if (err) um.onUnhandledErr(err)
  }
}

/*
This promise wrapper allows us to detect changes in `obs.task` and disregard
the result of any outdated task. This usually happens when the previous task
is aborted. TODO figure out an elegant way to perform such detection without
a promise wrapper.
*/
class ObsTask extends a.Emp {
  #task = undefined

  constructor(obs, src) {
    super()
    a.reqInst(obs, TaskObs)

    const task = this.#run(obs, src)
    task.catch(a.nop)
    this.#task = task
  }

  // Implement the promise interface. Compatible with `async/await`.
  then(...val) {return this.#task.then(...val)}

  // Allows to wait to completion, ignoring errors and returning void.
  async wait() {try {await this.#task} catch {}}

  async #run(obs, src) {
    try {
      const out = await src
      if (obs.task !== this) return undefined
      return obs.onValue(out)
    }
    catch (err) {
      if (obs.task !== this) return undefined
      obs.onError(err)
      throw err
    }
  }
}

class TaskObsPh extends ob.ObsPh {
  get(tar, key) {
    if (a.hasOwnEnum(tar, key)) tar.readAt = Date.now()
    return super.get(...arguments)
  }
}

export class FetchObs extends TaskObs {
  constructor(...args) {
    super()
    a.priv(this, `args`, args)
  }

  fetch() {throw ue.errImplementInSubclass()}

  /*
  We must ensure that this always creates a promise. If the fetch function
  creates a synchronous exception, we store a rejected promise. This allows
  aggregates such as `Promise.all` to "capture" multiple promises even when
  some of them are rejected immediately. If we don't capture synchronous
  exceptions, it's possible to have a situation where we create multiple
  promises, some of them are rejected, then a SYNCHONOUS exception prevents
  us from calling `Promise.all`, and unhandled rejections crash the server.
  */
  refetch() {
    if (!this.task) {
      const init = async () => this.fetch(...this.args)
      this.setTask(init())
    }
    return this.task
  }

  refetchFresherThan(freshness) {if (this.isOutdated(freshness)) this.refetch()}

  maybeRefetch() {if (!this.hasValue()) this.refetch()}

  /*
  Reuses an existing task or value, but NOT an existing error. If fetching
  fails, we retry again. There is no retry limit. To make fetching "optional"
  and stop trying after the first error, don't throw in the fetch method.
  */
  valueTask() {
    if (this.task) return this.task
    if (this.hasValue()) return Promise.resolve(this.value)
    return this.valueTaskFresh()
  }

  valueTaskFresh() {return this.refetch()}

  valueTaskFresherThan(freshness) {
    this.refetchFresherThan(freshness)
    return this.valueTask()
  }

  // TODO deprecate and remove.
  valueTaskBg() {
    this.refetch()
    return this.valueTask()
  }
}

// Variant of `FetchObs` with slightly more aggressive refetching.
export class EagerFetchObs extends FetchObs {
  valueTask() {
    this.refetchFresherThan(c.FRESHNESS_DEFAULT)
    return super.valueTask()
  }
}

export class ClsFetchObs extends MixClsTaskObs(EagerFetchObs) {}

function MixClsTaskObs(cls) {
  return class ClsTaskObsCls extends cls {
    get cls() {return Object}
    setValue(val) {return super.setValue(this.make(val))}
    make(val) {return a.toInstOpt(val, this.cls)}
  }
}

/*
Shortcut for defining a "fetch observable" by passing a fetch function, without
subclassing and overriding the fetch method.
*/
export class FunFetchObs extends EagerFetchObs {
  constructor(fetch, ...args) {
    a.reqFun(fetch)
    super(...args)
    a.priv(this, `fetch`, fetch)
  }
}

export class ClsFunFetchObs extends MixClsTaskObs(FunFetchObs) {}

/*
Wraps a "fetch observable", lazily transforming its value. Usage:

  const obs = u.FetchObsComp(
    someOtherObs,
    function transform(value) {return transformedValue},
  )

  const {task, error, value: transformedValue} = obs

Keep in mind that the computation is meant to be initialized once and accessed
many times. It should be created statically on `ctx.dat` or in a view component
constructor.

The inner observable must have the same methods as `FetchObs` or `FetchObsComp`.
*/
export class FetchObsComp extends a.Emp {
  constructor(ref, fun) {
    super()
    a.priv(this, `ref`, a.req(ref, isFetchObs))
    a.priv(this, `fun`, a.reqFun(fun))
    a.priv(this, `val`)
    a.priv(this, `setAt`)
  }

  get value() {
    // This must be accessed unconditionally, before all other calculations.
    const value = this.ref.value
    const setAt = this.ref.setAt

    if (a.isNil(setAt)) {
      this.setAt = undefined
      return this.val = undefined
    }

    if (setAt > a.laxFin(this.setAt)) {
      this.setAt = setAt
      this.val = this.fun(value)
    }
    return this.val
  }

  get error() {return this.ref.error}
  get task() {return this.ref.task}

  setValue(val) {return this.ref.setValue(val), this.value}
  setError(val) {this.ref.setError(val)}
  setTask(val) {return this.ref.setTask(val), this.valueTask()}

  async valueTask(...val) {return (await this.ref.valueTask(...val)), this.value}
  async valueTaskBg(...val) {return (await this.ref.valueTaskBg(...val)), this.value}
  async valueTaskFresh(...val) {return (await this.ref.valueTaskFresh(...val)), this.value}
  async valueTaskFresherThan(...val) {return (await this.ref.valueTaskFresherThan(...val)), this.value}
  async refetch(...val) {return (await this.ref.refetch(...val)), this.value}
  async refetchFresherThan(...val) {return (await this.ref.refetchFresherThan(...val)), this.value}
}

function isFetchObs(val) {
  return (
    a.hasMeth(val, `valueTask`) &&
    `value` in val &&
    `error` in val &&
    `task` in val &&
    `setAt` in val
  )
}

// TODO: replace all uses of `ClsFeedObs` with this.
export class ClsPageObs extends EagerFetchObs {
  constructor(ctx, params, set) {
    super(ctx, params)
    um.privs(this, {ctx, params, set})
  }

  get cls() {return ufe.ClsPage}

  setValue(val) {
    if (a.isNil(val)) return super.setValue(val)

    const page = new this.cls({params: this.params, ...val})

    /*
    Optional normalization: deduplicating items between this feed/page, and an
    external "maker" of "task observables". This reduces memory usage when many
    feeds/pages share identical items, by deduplicating and using them by
    reference.

    This is currently not implemented in `ClsFeedObs`, because instead of having
    a single array of `items`, it has multiple pages, and the current API
    doesn't make it easy to perform normalization only for new pages. We should
    simply replace "feeds" with individual "pages" everywhere.
    */
    if (this.set) {
      a.values(page.getEnts()).forEach(this.set.add, this.set)
    }

    return super.setValue(page)
  }
}

// Deprecated, use the buket API for newer code.
export class ClsFeedObs extends ClsFetchObs {
  constructor(ctx, params) {
    a.reqStruct(ctx)
    params = a.laxDict(params)
    super()
    um.privs(this, {ctx, params})
  }

  get cls() {return ufe.ClsFeed}
  first() {throw ue.errImplementInSubclass()}
  next() {throw ue.errImplementInSubclass()}

  /*
  Fetches or re-fetches the first page. If the feed was longer, truncates it.

  The reason for synchronously truncating the existing feed is that it will be
  truncated anyway when refetched; if the feed was long, and if the refetch is
  done in the background, this would cause the feed to suddenly shorten, which
  is jarring. Refetching a feed usually happens when transitioning between
  pages; specifically, an existing feed is reused when going back in the
  browser history. With this behavior, going back should render a short feed
  right away and avoid jarring layout jumps.
  */
  fetch() {
    const {params} = this

    if (this.value) {
      this.setValue(this.value.truncatedToFirstPage())
    }
    else {
      this.setValue(new this.cls({params}))
    }

    return this.first(this.ctx, this.value)
  }

  /*
  Fetches the next page for the feed, if possible.

  Known issue: if this is called during the initial fetch, the resulting task
  will wait for that fetch, and the next page won't be fetched at all. Currently
  unclear what would be the ideal behavior.
  */
  fetchNext() {
    if (this.task) return

    const feed = this.value
    if (!feed) {
      this.refetch()
      return
    }

    this.setTask(this.next(this.ctx, feed))
  }

  fetchNextTask() {
    this.fetchNext()
    return this.valueTask()
  }
}

export class FunFeedFetchObs extends ClsFeedObs {
  constructor(ctx, first, next, params) {
    super(ctx, params)
    a.reqFun(first)
    a.reqFun(next)
    um.privs(this, {first, next})
  }
}

export function hasSubs(ref) {return a.hasLen(ob.ph(ref)?.obs)}

export function mutPaused(tar, src) {
  return ob.Sched.main.paused(a.patch, tar, src)
}

export class BukPageFetchObs extends EagerFetchObs {
  key() {return c.BUK_KEY_MAIN}
  inp() {throw ue.errImplementInSubclass()}
  page() {throw ue.errImplementInSubclass()}

  async fetch(ctx, inp) {
    const buk = await ctx.pageDat.bukFetch(this.inp(inp))
    const out = this.page(buk)
    a.reqInst(out, ufe.PageOrFeed)
    out.setParams?.(inp)
    return out
  }

  pageInp(src) {
    return {
      ...a.optStruct(src),
      key: this.key(),
      contentType: c.CONTENT_TYPE_JSON_QUERY,
    }
  }
}

// TODO better name.
export class IniterObs extends TaskObsNotif {
  init() {
    if (this.hasValue()) return this.value
    if (this.hasTask()) return this.task
    return this.setTask(this.initAsync())
  }

  async initAsync() {}
}

/*
Idempotently wraps the given object into observable proxy.
TODO move to `@mitranim/js/obs.mjs`.
*/
export function toObs(val) {
  a.reqObj(val)
  if (ob.isObs(val)) return val
  if (ob.isObs(ob.ph(val))) return val
  return ob.obs(val)
}

// TODO move to `@mitranim/js/obs.mjs`.
export function toObsOpt(val) {
  return a.isNil(val) ? undefined : toObs(val)
}
